import React, { useState, useEffect, useCallback } from "react";
import {
  TextField,
  Grid,
  FormControl,
  Autocomplete,
  CircularProgress,
  InputAdornment,
  Box,
  Card,
  CardContent,
  Typography,
  Divider,
  createTheme,
  ThemeProvider,
  useTheme,
} from "@mui/material";
import {
  Business as BusinessIcon,
  Person as PersonIcon,
  AccountTree as AccountTreeIcon,
  Category as CategoryIcon,
  Search as SearchIcon,
} from "@mui/icons-material";
import { useAuth } from "../login/AuthContext";
import axios from "axios";
import config from "../../config/config";
import { toast } from "react-toastify";
import { Search } from "@mui/icons-material";
import { debounce } from "lodash";
import { outlinedInputClasses } from "@mui/material/OutlinedInput";

const DealerForm = ({ onDealerCreated, editingDealer, fetchDealers }) => {
  const { token } = useAuth();
  const [customers, setCustomers] = useState([]);
  const [parentDealers, setParentDealers] = useState([]);
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [selectedParentDealer, setSelectedParentDealer] = useState(null);

  const [loading, setLoading] = useState(false);
  const [searchMessage, setSearchMessage] = useState("");
  const [dealerAuthritys, setDealerAuthritys] = useState([]);
  const [selectedDealerAuthritys, setSelectedtDealerAuthritys] = useState(null);

  const [formData, setFormData] = useState({
    BayiAdi: "",
    Kodu: "",
    BayiTuru: "",
    MusteriId: null,
    UstBayiId: null
  });


  const resetForm = () => {
    setFormData({
      BayiAdi: "",
      Kodu: "",
      BayiTuru: "",
      MusteriId: null,
      UstBayiId: null,
      YetkiTuru: null,
    });
    setSelectedCustomer(null);
    setSelectedParentDealer(null);
    setSelectedtDealerAuthritys(null);
    setCustomers([]);
    setSearchMessage("");
  };


  const debouncedSearch = useCallback(
    debounce(async (query) => {
      if (!query || query.length < 3) {
        setCustomers([]);
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get(
          `${config.baseURL}/musteriler/search`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
            params: {
              query: query,
              searchFields: ['Unvan', 'Yetkili']  // Hem ünvan hem yetkili adında ara
            }
          }
        );
        setCustomers(response.data.data);
      } catch (error) {
        console.error("Arama hatası:", error);
        toast.error("Müşteri araması yapılırken bir hata oluştu");
      } finally {
        setLoading(false);
      }
    }, 300),
    [token]
  );

  // Üst bayileri getir
  const fetchParentDealers = async () => {
    try {
      const res = await axios.get(`${config.baseURL}/bayiler/ust-bayiler`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (res.status === 200) {
        const dealersData = Array.isArray(res.data) ? res.data : res.data.data;
        setParentDealers(dealersData);
      }
    } catch (error) {
      console.error("Error fetching parent dealers:", error);
      toast.error("Üst bayiler yüklenirken hata oluştu");
    }
  };

  const fetchDealerAuthritys = async () => {
    try {
      const res = await axios.get(`${config.baseURL}/bayiler/bayi-yetki/bayi-yetki-tur`, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      if (res.status === 200) {
        const dealerAuthritys = Array.isArray(res.data) ? res.data : res.data.data;
        setDealerAuthritys(dealerAuthritys);
      }
    } catch (error) {
      console.error("Error fetching dealer authorities:", error);
      toast.error("Bayi yetkileri yüklenirken hata oluştu");
    }
  }

  useEffect(() => {
    fetchParentDealers();
    fetchDealerAuthritys();
  }, []);

  useEffect(() => {
    if (editingDealer && dealerAuthritys.length > 0 && parentDealers.length > 0) {
      // Extract the actual dealer data, whether it's nested or not
      const dealerData = editingDealer.data || editingDealer;

      // console.log("Editing dealer data:", dealerData);

      setFormData({
        BayiAdi: dealerData.BayiAdi || "",
        Kodu: dealerData.Kodu || "",
        BayiTuru: dealerData.BayiTuru || "",
        MusteriId: dealerData.MusteriKodu || null,
        UstBayiId: dealerData.UstBayi || null,
        YetkiTuru: dealerData.YetkiTuru || null,
      });

      // Set customer data
      if (dealerData.MusteriKodu) {
        axios.get(`${config.baseURL}/musteriler/${dealerData.MusteriKodu}`, {
          headers: { Authorization: `Bearer ${token}` }
        })
        .then(response => {
          const customerData = response.data.data || response.data;
          // console.log("Loaded customer data:", customerData);
          setSelectedCustomer(customerData);
        })
        .catch(error => console.error("Müşteri bilgisi alınamadı:", error));
      }

      // Set parent dealer
      if (dealerData.UstBayi) {
        const parentDealer = parentDealers.find(d => d.Id === dealerData.UstBayi);
        if (parentDealer) {
          // console.log("Found parent dealer:", parentDealer);
          setSelectedParentDealer(parentDealer);
        } else {
          console.warn("Parent dealer not found with ID:", dealerData.UstBayi);
        }
      }

      // Set dealer authority (Bayiler.YetkiTuru = BayiYetkiTur.YetkiTuru: 0,1,2,3)
      if (dealerData.YetkiTuru != null) {
        const dealerAuthority = dealerAuthritys.find(a => a.YetkiTuru === dealerData.YetkiTuru);
        if (dealerAuthority) {
          setSelectedtDealerAuthritys(dealerAuthority);
        } else {
          console.warn("Dealer authority not found with YetkiTuru:", dealerData.YetkiTuru);
        }
      }
    }
  }, [editingDealer, parentDealers, dealerAuthritys, token]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!selectedCustomer) {
      toast.error("Lütfen bir müşteri seçin");
      return;
    }

    try {
      const submitData = {
        ...formData,
        MusteriKodu: selectedCustomer?.Id,
        Kodu: selectedCustomer?.MusteriNo,
        UstBayiId: selectedParentDealer?.Id,
        YetkiTuru: selectedDealerAuthritys?.YetkiTuru ?? null,
      };

      let response;

      if (editingDealer) {
        const dealerId = editingDealer.Id || editingDealer.data?.Id;
        response = await axios.put(
          `${config.baseURL}/bayiler/${dealerId}`,
          submitData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.success("Bayi başarıyla güncellendi");
      } else {
        response = await axios.post(
          `${config.baseURL}/bayiler`,
          submitData,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        toast.success("Bayi başarıyla eklendi");
      }

      if (response.status === 200 || response.status === 201) {
        onDealerCreated(response.data);
        // fetchDealers();
        resetForm();
      }
    } catch (error) {
      console.error("Error saving dealer:", error);
      toast.error(error.response?.data?.message || "Bayi kaydedilirken bir hata oluştu");
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };


  const outerTheme = useTheme();

  const customTheme = (outerTheme) =>
    createTheme({
      palette: {
        ...outerTheme.palette,
        mode: outerTheme.palette.mode,
      },
      typography: {
        ...outerTheme.typography,
      },
      components: {
        MuiTextField: {
          styleOverrides: {
            root: {
              "--TextField-brandBorderColor": outerTheme.palette.mode === 'dark' ? "#424242" : "#D0D0D0",
              "--TextField-brandBorderHoverColor": outerTheme.palette.mode === 'dark' ? "#616161" : "#9E9E9E",
              "--TextField-brandBorderFocusedColor": outerTheme.palette.mode === 'dark' ? "#757575" : "#616161",
              "& label.Mui-focused": {
                color: "var(--TextField-brandBorderFocusedColor)",
              },
            },
          },
        },
        MuiOutlinedInput: {
          styleOverrides: {
            notchedOutline: {
              borderColor: "var(--TextField-brandBorderColor)",
            },
            root: {
              [`&:hover .${outlinedInputClasses.notchedOutline}`]: {
                borderColor: "var(--TextField-brandBorderHoverColor)",
              },
              [`&.Mui-focused .${outlinedInputClasses.notchedOutline}`]: {
                borderColor: "var(--TextField-brandBorderFocusedColor)",
              },
              "& input": {
                fontSize: "calc(1.7vh * 0.9)", // ~15.3px (1.7vh * 0.9)
              },
              "& textarea": {
                fontSize: "calc(1.7vh * 0.9)", // ~15.3px (1.7vh * 0.9)
              },
            },
          },
        },
        MuiSelect: {
          styleOverrides: {
            root: {
              "& .MuiSelect-select": {
                fontSize: "calc(1.7vh * 0.9)", // ~15.3px (1.7vh * 0.9)
              },
            },
          },
        },
        MuiMenuItem: {
          styleOverrides: {
            root: {
              fontSize: "calc(1.7vh * 0.9)", // ~15.3px (1.7vh * 0.9)
            },
          },
        },
        MuiCard: {
          styleOverrides: {
            root: {
              boxShadow: outerTheme.palette.mode === 'dark'
                ? "0 1px 3px rgba(0,0,0,0.3)"
                : "0 1px 3px rgba(0,0,0,0.08)",
              borderRadius: "8px",
              border: outerTheme.palette.mode === 'dark'
                ? "1px solid rgba(255,255,255,0.08)"
                : "1px solid rgba(0,0,0,0.06)",
              backgroundColor: outerTheme.palette.mode === 'dark'
                ? outerTheme.palette.background.paper
                : "#FFFFFF",
            },
          },
        },
        MuiDivider: {
          styleOverrides: {
            root: {
              borderColor: outerTheme.palette.mode === 'dark'
                ? "rgba(255,255,255,0.08)"
                : "rgba(0,0,0,0.08)",
            },
          },
        },
      },
    });

  const SectionHeader = ({ icon: Icon, title }) => (
    <Box display="flex" alignItems="center" gap={1.5} mb={1.5}>
      <Icon
        sx={{
          color: outerTheme.palette.mode === 'dark' ? 'rgba(255,255,255,0.6)' : 'rgba(0,0,0,0.54)',
          fontSize: '20px'
        }}
      />
      <Typography
        variant="subtitle1"
        fontWeight={700}
        color="text.secondary"
        sx={{
          letterSpacing: '0.02em',
          textTransform: 'uppercase',
          fontSize: '0.75rem'
        }}
      >
        {title}
      </Typography>
    </Box>
  );

  return (
    <ThemeProvider theme={customTheme(outerTheme)}>
      <Box
        sx={{
          height: '100%',
          overflowY: 'auto',
          overflowX: 'hidden',
        }}
      >
        <form id="bayikaydetform" onSubmit={handleSubmit}>
          <Card elevation={0} sx={{ mb: 1.5 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <SectionHeader icon={PersonIcon} title="Müşteri Bilgileri" />
              <Divider sx={{ mb: 2, mt: 0.5 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <Autocomplete
                      value={selectedCustomer}
                      onChange={(event, newValue) => {
                        setSelectedCustomer(newValue);
                      }}
                      onInputChange={(event, newValue) => {
                        if (newValue.length < 3) {
                          setSearchMessage("En az 3 karakter giriniz");
                          return;
                        }
                        setSearchMessage("");
                        debouncedSearch(newValue);
                      }}
                      filterOptions={(x) => x}
                      options={customers}
                      getOptionLabel={(option) => `${option.Unvan}${option.Yetkili ? ` - ${option.Yetkili}` : ""}`}
                      renderOption={(props, option) => (
                        <li {...props}>
                          <div>
                            <div style={{ fontWeight: 'bold' }}>{option.Unvan}</div>
                            {option.Yetkili && (
                              <div style={{ fontSize: '0.8em', color: 'grey' }}>
                                Yetkili: {option.Yetkili}
                              </div>
                            )}
                          </div>
                        </li>
                      )}
                      isOptionEqualToValue={(option, value) => option.Id === value.Id}
                      loading={loading}
                      noOptionsText={searchMessage || "Sonuç bulunamadı"}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Müşteri Seç"
                          required
                          helperText={searchMessage}
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <SearchIcon />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                            endAdornment: (
                              <>
                                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                                {params.InputProps.endAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card elevation={0} sx={{ mb: 1.5 }}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <SectionHeader icon={BusinessIcon} title="Bayi Bilgileri" />
              <Divider sx={{ mb: 2, mt: 0.5 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <TextField
                      label="Bayi Adı"
                      name="BayiAdi"
                      value={formData.BayiAdi}
                      onChange={handleChange}
                      required
                      InputProps={{
                        startAdornment: (
                          <InputAdornment position="start">
                            <BusinessIcon color="action" fontSize="small" />
                          </InputAdornment>
                        ),
                      }}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <FormControl fullWidth>
                    <Autocomplete
                      value={selectedDealerAuthritys}
                      onChange={(event, newValue) => {
                        setSelectedtDealerAuthritys(newValue);
                      }}
                      options={dealerAuthritys}
                      getOptionLabel={(option) => option.YetkiAdi || ""}
                      isOptionEqualToValue={(option, value) => option.YetkiTuru === value?.YetkiTuru}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Bayi Türü Seç"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <CategoryIcon color="action" fontSize="small" />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>

          <Card elevation={0}>
            <CardContent sx={{ p: 2, '&:last-child': { pb: 2 } }}>
              <SectionHeader icon={AccountTreeIcon} title="Hiyerarşi Bilgileri" />
              <Divider sx={{ mb: 2, mt: 0.5 }} />
              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <FormControl fullWidth>
                    <Autocomplete
                      value={selectedParentDealer}
                      onChange={(event, newValue) => {
                        setSelectedParentDealer(newValue);
                      }}
                      options={parentDealers}
                      getOptionLabel={(option) => option.BayiAdi || ""}
                      isOptionEqualToValue={(option, value) => option.Id === value.Id}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          label="Üst Bayi Seç"
                          InputProps={{
                            ...params.InputProps,
                            startAdornment: (
                              <>
                                <InputAdornment position="start">
                                  <AccountTreeIcon color="action" fontSize="small" />
                                </InputAdornment>
                                {params.InputProps.startAdornment}
                              </>
                            ),
                          }}
                        />
                      )}
                    />
                  </FormControl>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </form>
      </Box>
    </ThemeProvider>
  );
};

export default DealerForm;